import { getTheme, Button, FlexFlow, Flow, Stream, Scrollable, } from "@bracket/ui";
import gsap from "gsap";
import { type ReactNode, useLayoutEffect, useRef, useState, } from "react";
import styled from "styled-components";
import { useOnClickOutside } from "usehooks-ts";
import closeModalIcon from "../closeModal.png";
import { useModal } from "../ModalProvider";


export declare type ModalProps = {
	head: string;
	detail?: string;
	children: ReactNode;
	hideCloseIcon?: boolean;
	hasConfirmed?: boolean;
	hasConfirmedMessage?: string;
	disableOutsideClose?: boolean;
	onConfirm?: () => Promise<void> | void;
	onConfirmLabel?: string;
	onClose?: () => Promise<void> | void;
	onCloseLabel?: string;
	enforceRead?: boolean;
	enforceReadTest?: () => boolean;
	height?: string;
	width?: string;
};

export const Modal = ({
	head,
	detail,
	children,
	hideCloseIcon = false,
	disableOutsideClose = false,
	hasConfirmed = false,
	hasConfirmedMessage,
	onConfirm = () => {},
	onConfirmLabel = "Confirm",
	onClose = () => {},
	onCloseLabel = "Close",
	enforceRead = false,
	enforceReadTest = () => true,
	height,
	width,
	...props
}: ModalProps) => {

	const ref = useRef<HTMLDivElement|null>(null);
	const modalBodyRef = useRef<HTMLDivElement|null>(null);
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const { closeModal, } = useModal();

	// Fade in animation on mount
	useLayoutEffect(() => {
		if (ref.current) {
			gsap.fromTo(ref.current, 
				{ opacity: 0 },
				{ opacity: 1, duration: 0.325, ease: "power2.out" }
			);
		}
	}, []);

	// Handle fade out animation before closing
	const handleClose = async () => {
		if (ref.current) {
			await gsap.to(ref.current, {
				opacity: 0,
				duration: 0.325,
				ease: "power2.in"
			});
		}
		await closeModal();
	};

	useOnClickOutside(ref, disableOutsideClose ? () => {} : handleClose);

	/**
	 * Handles scroll events on the modal body to detect when user has scrolled to bottom
	 */
	const handleScroll = () => {
		if(!modalBodyRef.current || !enforceRead) return;
		const { scrollTop, scrollHeight, clientHeight, } = modalBodyRef.current;
		const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
		const testPassed = enforceReadTest();
		if(isAtBottom && testPassed && !hasScrolledToBottom) {
			setHasScrolledToBottom(true);
		}
	};

	const handleConfirm = async () => {
		await onConfirm();
		await handleClose();
	};

	const isConfirmDisabled = enforceRead && !hasScrolledToBottom;

	return (
		<ModalContainer ref={ref} $height={height} $maxWidth={width} {...props}>
			<ModalContent>
				{ !hideCloseIcon && <ModalCloseIcon src={closeModalIcon} onClick={handleClose} /> }
				<ModalHeader>
					<Heading>{head}</Heading>
					{ detail && <Detail>{detail}</Detail> }
				</ModalHeader>
				<ModalBodyWrapper>
					<Scrollable onScroll={handleScroll} ref={modalBodyRef}>
						<ModalChildren onClick={(e) => e.stopPropagation()}>
							{children}
						</ModalChildren>
					</Scrollable>
				</ModalBodyWrapper>
				<ModalFooter onClick={(e) => e.stopPropagation()}>
					{ hasConfirmed && hasConfirmedMessage && <FinePrint>{hasConfirmedMessage}</FinePrint> }
					{ !hasConfirmed && (
						<>
						<ButtonGroup>
							<Button label={onCloseLabel} variant="primary-outline" onClick={handleClose} disabled={hasConfirmed} />
							<Button label={onConfirmLabel} variant="primary" onClick={handleConfirm} disabled={isConfirmDisabled || hasConfirmed} />
						</ButtonGroup>					
						{ isConfirmDisabled && <FinePrint>Please scroll to the bottom to enable confirmation</FinePrint> }
						</>
					) }
				</ModalFooter>
			</ModalContent>
		</ModalContainer>
	);
};



const Text = styled.h5`font-family: 'Space Grotesk', sans-serif; font-size: 24px; line-height: 32px; color: #F0F1EC; font-weight: 500;`
const Text2 = styled.p` font-family: 'Inter', sans-serif; font-size: 16x; line-height: 20px; color: ${() => getTheme().subheading}; font-weight: 400;`
const Heading  = styled(Text)``
const Detail  = styled(Text2)``


const ModalContainer = styled(Flow)<{ $height?: string; $maxWidth?: string }>`
  background-color: ${() => getTheme().surface};
	position: fixed;
	border-radius: 8px;
  width: ${({ $maxWidth }) => $maxWidth || "80%"};
  max-width: none;
  max-height: 90vh;
  overflow: hidden;
	opacity: 0;
	height: ${({ $height }) => $height || "80%"};
`;

const ModalContent = styled(FlexFlow)`
	padding-right: 1.25rem;
	position: relative;
	min-height: 0;
`;

const ModalHeader = styled(Flow)`
	padding: 32px;
`;

const ModalFooter = styled(Flow)`
	align-items: flex-end;
	gap: 0.5rem;
	background-color: ${() => getTheme().surface};
	padding: 2rem;
`;

const ButtonGroup = styled(Stream)`
	gap: 1rem;
`;

const FinePrint = styled.p`
	font-family: ${() => getTheme().fonts.body};
	font-size: 12px;
	color: ${() => getTheme().subheading};
	font-weight: 400;
	margin: 0;
	font-style: italic;
`;


const ModalChildren = styled(Flow)`
	padding: 2rem;
	background: ${() => getTheme().surface};
	border-radius: 1rem;
	gap: 1rem;
`;

const ModalCloseIcon = styled.img`
	position: absolute;
	cursor: pointer;
	right: 1rem;
	top: 1rem;x
	&:hover {
		filter: brightness(0.8);
	}
`;

const ModalBodyWrapper = styled(FlexFlow)`
	position: relative;
  background-color: ${() => getTheme().surface};
	padding: 0 2rem;
	min-height: 0;
`;
